<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useRoute } from 'vue-router'
import DToastStack from '../../../components/notifications/DToastStack.vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DTextField,
  DLoading,
  DToggleField,
  InfoCircleIcon
} from 'front-library'
import {
  sendCode,
  SendCode,
  sendReport,
  SendReport,
  SendApproval,
  sendApprovalPayment
} from '../../../core/services/payroll-receipts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(utc)
dayjs.extend(timezone)
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const route = useRoute()
const path = route.query.data as string
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente DModal */
const showModal = ref(false)

/* Datos componente DLoading */
const loading = ref(false)
const messageLoading = ref('')

/* Datos formulario */
const urlFile = ref<string>('')
const approval = ref<boolean>(false)
const allowApproval = ref<boolean>(false)
const dateApproval = ref<string>('')
const isViewPassword = ref(false)

// --- Helpers con TTL ---
const keySuffix = () => {
  const raw = route.query.data
  return typeof raw === 'string' && raw.length > 0 ? raw : 'global'
}
const kCooldown = () => `pr_code_cooldown_until_${keySuffix()}`
const kRequested = () => `pr_code_requested_${keySuffix()}`

const lsSetTTL = (key: string, value: any, ttlMs: number) => {
  const payload = { v: value, exp: Date.now() + ttlMs }
  localStorage.setItem(key, JSON.stringify(payload))
}
const lsGetTTL = <T = any>(key: string): T | null => {
  const raw = localStorage.getItem(key)
  if (!raw) return null
  try {
    const { v, exp } = JSON.parse(raw)
    if (typeof exp === 'number' && Date.now() > exp) {
      localStorage.removeItem(key)
      return null
    }
    return v as T
  } catch {
    localStorage.removeItem(key)
    return null
  }
}
/* --- NUEVO: control de flujo y cooldown --- */
const COOLDOWN_SECONDS = 60
const REQUESTED_TTL_MS = 60 * 1000

const hasRequestedCode = ref<boolean>(false)
const cooldownRemaining = ref<number>(0)
let intervalId: number | undefined

const setCooldownUntil = (until: number) => {
  localStorage.setItem(kCooldown(), String(until))
}
const getCooldownUntil = (): number => {
  const raw = localStorage.getItem(kCooldown())
  if (!raw) return 0
  const n = Number(raw)
  if (Number.isFinite(n)) return n
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.v !== 'undefined') {
      const maybe = Number(parsed.v)
      if (Number.isFinite(maybe)) return maybe
    }
  } catch {
    /* ignore */
  }
  localStorage.removeItem(kCooldown())
  return 0
}

function loadCooldownFromStorage() {
  const until = Number(localStorage.getItem(kCooldown()) || '0')
  const now = Date.now()
  if (until > now) {
    cooldownRemaining.value = Math.ceil((until - now) / 1000)
    startTick()
    hasRequestedCode.value = true
  } else {
    cooldownRemaining.value = 0
    if (until) localStorage.removeItem(kCooldown())
  }
  hasRequestedCode.value = hasRequestedCode.value || !!lsGetTTL(kRequested())
}

function startCooldown() {
  const until = Date.now() + COOLDOWN_SECONDS * 1000
  setCooldownUntil(until)
  lsSetTTL(kRequested(), true, REQUESTED_TTL_MS)
  hasRequestedCode.value = true
  cooldownRemaining.value = COOLDOWN_SECONDS
  startTick()
}

function startTick() {
  stopTick()
  intervalId = window.setInterval(() => {
    const until = getCooldownUntil()
    const now = Date.now()
    if (until <= now) {
      cooldownRemaining.value = 0
      stopTick()
      if (until) localStorage.removeItem(kCooldown())
      return
    }
    cooldownRemaining.value = Math.ceil((until - now) / 1000)
  }, 250)
}
function stopTick() {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = undefined
  }
}

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object({
  autocode: string()
    .required(t('form.valid.required'))
    .matches(/^[(0-9)]+$/g, t('form.valid.numericOnly'))
    .min(6, t('form.valid.minLength', [6]))
    .max(6, t('form.valid.maxLength', [6]))
})

/* Uso del metodo de validaión, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors, resetForm } = useForm<any>({
  validationSchema
})

/*  */
const validateForm = async (values: any) => {
  loading.value = true
  messageLoading.value = t('paymentReceipts.loading.validatingCode')
  urlFile.value = ''
  approval.value = false
  allowApproval.value = false
  try {
    const payloadSendReport: SendReport = {
      data: path,
      autocode: values.autocode
    }
    const response = await sendReport(payloadSendReport)

    if (response.url) {
      urlFile.value = response.url
      approval.value = response.approval
      dateApproval.value = response.date
      allowApproval.value = response.allowApproval
    } else {
      showError(response)
    }
  } catch (error: any) {
    showError(error)
  }
  loading.value = false
}

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit(values => {
  validateForm(values)
})

/* Definicion de  campos del formulario */
const { value: autocode } = useField<string>(ref('autocode'))

/* Metodo para cerrar el componente modal */
const closeModal = () => {
  showModal.value = false
}

/* Metodo para enviar el código por correo */
const sendCodeEmail = async () => {
  loading.value = true
  messageLoading.value = t('paymentReceipts.loading.sendingCode')
  try {
    const payloadSendCode: SendCode = {
      code: 'payroll-receipt/validate',
      data: path
    }
    const result = await sendCode(payloadSendCode)
    startCooldown()
    console.log(result)
    loading.value = false
    notifications.addNotification({
      title: t('paymentReceipts.notification.codeSentTitle'),
      text: t('paymentReceipts.notification.codeSentText'),
      type: 'success',
      time: 10000
    })
  } catch (error: any) {
    showError(error)
  }
}

/* Metodo para enviar la aprobación */
const sendApproval = async () => {
  loading.value = true
  messageLoading.value = t('paymentReceipts.loading.sendingApproval')
  try {
    const payloadSendApproval: SendApproval = {
      data: path
    }
    const response = await sendApprovalPayment(payloadSendApproval)
    if (response) {
      approval.value = true
      dateApproval.value = response.date
    } else {
      showError(response)
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: 'Error',
    text: error,
    type: 'error',
    time: 10000
  })
}

onMounted(() => {
  loadCooldownFromStorage()
})

onBeforeUnmount(() => stopTick())
</script>
<template>
  <DToastStack class="left-0" />
  <div class="h-screen w-screen p-4 bg-gray-50">
    <DLoading v-if="loading" :message="messageLoading" />
    <div
      v-if="!urlFile"
      class="flex flex-col gap-5 justify-center items-center h-full w-full">
      <div>
        <img
          src="/src/assets/logo.png"
          class="h-20 static logo-aps"
          alt="Logo APS" />
      </div>
      <form @submit.prevent="onSubmit">
        <DCard :footer="true" :header="true">
          <!-- Header -->
          <template #header>
            <h1
              class="tracking-wide font-medium text-center"
              v-html="t('paymentReceipts.viewPaymentReceipt.title')" />
          </template>

          <!-- Body -->
          <template #default>
            <!-- Paso 1: pedir/generar código -->
            <div v-if="!hasRequestedCode" class="flex flex-col gap-3">
              <div
                class="w-full p-5 bg-yellow-50 rounded-lg flex text-yellow-600 gap-3 items-center justify-between">
                <InfoCircleIcon class="h-8" />
                <p>{{ t('paymentReceipts.messages.requestCodeHint') }}</p>
              </div>
            </div>

            <!-- Paso 2: ingresar código -->
            <div v-else class="flex flex-col gap-3">
              <div
                class="w-full p-5 bg-yellow-50 rounded-lg flex text-yellow-600 gap-3 items-center justify-between">
                <InfoCircleIcon class="h-8" />
                <p>
                  {{ t('paymentReceipts.viewPaymentReceipt.description1') }}
                  <br />
                  {{ t('paymentReceipts.viewPaymentReceipt.description2') }}
                </p>
              </div>

              <hr />

              <DTextField
                v-model="autocode"
                :autofocus="hasRequestedCode"
                :label="t('paymentReceipts.fields.code')"
                :required="true"
                :type="`${isViewPassword == false ? 'password' : 'text'}`"
                :error="errors.autocode" />

              <DToggleField
                :label="t('paymentReceipts.fields.viewCode')"
                v-model="isViewPassword" />
            </div>
          </template>

          <!-- Footer -->
          <template #footer>
            <div class="flex flex-col gap-2 items-center justify-center">
              <!-- Botón paso 1 -->
              <DBtn
                v-if="!hasRequestedCode"
                :disabled="cooldownRemaining > 0 || loading"
                color="blue"
                class="w-full"
                @click="sendCodeEmail">
                {{
                  cooldownRemaining > 0
                    ? t('paymentReceipts.button.resendIn') +
                      ` ${cooldownRemaining}s`
                    : t('paymentReceipts.button.generateCode')
                }}
              </DBtn>

              <!-- Botones paso 2 -->
              <template v-else>
                <DBtn
                  color="blue"
                  class="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!autocode || autocode.length !== 6 || loading"
                  @click="onSubmit">
                  {{ t('paymentReceipts.button.validate') }}
                </DBtn>

                <button
                  type="button"
                  class="text-gray-500 cursor-pointer hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="cooldownRemaining > 0 || loading"
                  @click="sendCodeEmail">
                  {{
                    cooldownRemaining > 0
                      ? t('paymentReceipts.button.resendIn') +
                        ` ${cooldownRemaining}s`
                      : t('paymentReceipts.button.sendNewCode')
                  }}
                </button>
              </template>
            </div>
          </template>
        </DCard>
      </form>
    </div>
    <iframe v-if="urlFile" :src="urlFile" class="h-full w-full" />
    <DBtn
      v-if="urlFile && !approval && allowApproval"
      color="excel"
      class="fixed bottom-[5%] right-[5%] px-5 py-2 font-semibold"
      @click.prevent="sendApproval"
      v-html="t('paymentReceipts.button.sendApproval')" />
    <span
      v-if="urlFile && approval && allowApproval"
      class="fixed bottom-[5%] right-[5%] inline-flex items-center rounded-md bg-green-50 px-5 py-2 font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
      >{{
        t('paymentReceipts.button.approved') +
        ' ' +
        dayjs(dateApproval)
          .utc(true)
          .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
          .format('DD/MM/YYYY HH:mm:ss')
      }}</span
    >
  </div>
</template>
